import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarClock,
  Wallet,
  ChevronRight,
  MessageSquare,
  Snowflake,
  Layers,
  Repeat,
} from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { useDeshacer } from '@/hooks/deshacer-contexto';
import { cn } from '@/lib/cn';
import * as asuntosApi from '@/api/asuntos.api';
import * as inspectorApi from '@/api/inspector.api';
import type { Asunto } from '@/api/asuntos.api';
import type { TipoDeSenal } from '@/api/inspector.api';
import { HojaDelAsunto } from '@/components/athena/HojaDelAsunto';
import { TarjetaDeAsunto } from '@/components/athena/TarjetaDeAsunto';
import { Widget } from '@/components/athena/Widget';
import { TePregunto } from '@/components/athena/TePregunto';
import { QuienEs } from '@/components/athena/QuienEs';
import { hora12, fechaLarga, plata, plataPorMoneda, enCuanto } from '@/lib/athena';

/**
 * HOY — lo primero que veo.
 *
 * Mandato: *"En 3 segundos tengo que saber qué tengo hoy y qué es urgente."*
 * Todo lo de esta pantalla está subordinado a esos tres segundos.
 *
 * ═══ EL ORDEN NO ES DECORATIVO ═══
 *
 *   1. La frase de Athena — una línea que responde "¿qué pasa hoy?"
 *   2. Tres tarjetas grandes — urgente, próxima reunión, te deben
 *   3. Lo que se está escapando — lo que NO sabías
 *   4. Tu día — la línea de tiempo
 *
 * La frase va primera porque es lo único que se lee entero en tres segundos.
 * Un tablero de números obliga a interpretar; una frase ya interpretó.
 *
 * ═══ "LO QUE NECESITAS SABER" ANTES DE LA REUNIÓN ═══
 *
 * La tarjeta de la próxima reunión no dice sólo la hora: dice qué le debes y
 * qué te debe esa persona. Eso es lo que hace a una secretaria — llegar a la
 * reunión sabiendo, en vez de llegar puntual.
 */

const ICONO_SENAL: Record<TipoDeSenal, React.ReactNode> = {
  esperando: <MessageSquare className="h-4 w-4" />,
  enfriado: <Snowflake className="h-4 w-4" />,
  acumulado: <Layers className="h-4 w-4" />,
  repetido: <Repeat className="h-4 w-4" />,
};

function TarjetaGrande({
  icono,
  titulo,
  valor,
  pie,
  tono,
  onClick,
}: {
  icono: React.ReactNode;
  titulo: string;
  valor: string;
  pie?: string;
  tono: 'rojo' | 'azul' | 'verde';
  onClick: () => void;
}) {
  const tonos = {
    rojo: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-200',
    azul: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-200',
    verde: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200',
  };
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex min-h-[96px] w-full flex-col justify-between rounded-xl p-3 text-left transition active:scale-[0.98]',
        tonos[tono],
      )}
    >
      <span className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-medium opacity-80">
          {icono} {titulo}
        </span>
        <ChevronRight className="h-4 w-4 opacity-50" />
      </span>
      <span>
        <span className="block text-xl font-semibold leading-tight">{valor}</span>
        {pie && <span className="mt-0.5 block text-xs opacity-80">{pie}</span>}
      </span>
    </button>
  );
}

export default function Hoy() {
  const [abierto, setAbierto] = useState<string | null>(null);
  // Cual de los tres widgets esta abierto. Uno a la vez: dos cajas flotantes
  // superpuestas obligan a decidir cual cerrar antes de leer ninguna.
  const [widget, setWidget] = useState<'urgente' | 'proximo' | 'dinero' | null>(null);
  const navegar = useNavigate();
  const toast = useToast();
  const { ofrecerDeshacer } = useDeshacer();
  const qc = useQueryClient();

  const asuntos = useQuery({
    queryKey: ['asuntos', 'hoy'],
    queryFn: () => asuntosApi.getAsuntos({ limite: 200 }),
  });
  const senales = useQuery({
    queryKey: ['inspector', 'senales'],
    queryFn: inspectorApi.getSenales,
  });

  const mover = useMutation({
    mutationFn: ({ id, accion }: { id: string; accion: 'aprobar' | 'descartar' }) =>
      asuntosApi.moverAsunto(id, accion),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['asuntos'] });
      ofrecerDeshacer({
        texto: v.accion === 'aprobar' ? 'Aprobado' : 'Descartado',
        deshacer: async () => {
          await asuntosApi.moverAsunto(v.id, 'rescatar');
          void qc.invalidateQueries({ queryKey: ['asuntos'] });
          toast.success('Deshecho');
        },
      });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const lista: Asunto[] = asuntos.data?.asuntos ?? [];
  const ahora = new Date();
  const finDeHoy = new Date(ahora);
  finDeHoy.setHours(23, 59, 59, 999);

  const deHoy = lista.filter((a) => new Date(a.fechaCompromiso) <= finDeHoy);
  const urgentes = lista.filter((a) => a.prioridad === 'ALTA' || a.vencido);
  const proxima = lista
    .filter((a) => new Date(a.fechaCompromiso) > ahora)
    .sort(
      (x, y) => new Date(x.fechaCompromiso).getTime() - new Date(y.fechaCompromiso).getTime(),
    )[0];
  const dinero = lista.filter((a) => a.cajon === 'DINERO' && a.montoCentavos);
  // POR MONEDA, no en un solo numero: sumar pesos con dolares da un total
  // falso, y es el numero con el que el dueno decide a quien cobrarle.
  const totales = plataPorMoneda(dinero);

  // Qué tienes abierto con la persona de la próxima reunión. Es lo que
  // convierte "tienes una reunión" en "llegas sabiendo".
  const conEsaPersona = proxima
    ? lista.filter((a) => a.contactId === proxima.contactId && a.id !== proxima.id)
    : [];

  const frase = (() => {
    if (asuntos.isPending) return 'Revisando tu día…';
    const vencidos = lista.filter((a) => a.vencido).length;
    const partes: string[] = [];
    if (vencidos > 0)
      partes.push(`Tienes ${vencidos} ${vencidos === 1 ? 'cosa vencida' : 'cosas vencidas'}`);
    if (proxima)
      partes.push(
        `${proxima.conQuien} ${enCuanto(proxima.fechaCompromiso)} (${hora12(proxima.fechaCompromiso)})`,
      );
    if (partes.length === 0) return 'Hoy lo tienes limpio. Nada urgente por ahora.';
    return `${partes.join('. ')}.`;
  })();

  const listaSenales = senales.data?.senales ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 first-letter:uppercase dark:text-slate-50">
          {fechaLarga(ahora)}
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{frase}</p>
      </div>

      {/* ── LO QUE TE PREGUNTA ANTES DE GUARDARLO ──
          Va ARRIBA de las tarjetas a propósito: es lo único de esta pantalla
          que espera una respuesta. Si no hay nada que preguntar no dibuja
          nada, así que no le roba los tres segundos a nadie. */}
      <TePregunto />

      {/* "¿Quién es?": hasta 3 personas por día. Si no hay nadie, no se dibuja. */}
      <QuienEs />

      {/* ── LAS TRES TARJETAS ── */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <TarjetaGrande
          icono={<AlertTriangle className="h-4 w-4" />}
          titulo="Urgente"
          valor={String(urgentes.length)}
          pie={urgentes.length === 0 ? 'Nada urgente' : 'Requieren tu atención'}
          tono="rojo"
          onClick={() => setWidget('urgente')}
        />
        <TarjetaGrande
          icono={<CalendarClock className="h-4 w-4" />}
          titulo="Lo próximo"
          valor={proxima ? hora12(proxima.fechaCompromiso) : '—'}
          pie={proxima ? proxima.conQuien : 'Sin nada agendado'}
          tono="azul"
          onClick={() => setWidget('proximo')}
        />
        <TarjetaGrande
          icono={<Wallet className="h-4 w-4" />}
          titulo="Te deben"
          valor={totales[0] ?? 'RD$0.00'}
          pie={
            dinero.length === 0
              ? 'Nada por cobrar'
              : totales.length > 1
                ? `+ ${totales.slice(1).join(' · ')} · ${dinero.length} cobros`
                : `${dinero.length} cobros abiertos`
          }
          tono="verde"
          onClick={() => setWidget('dinero')}
        />
      </div>

      {/* ── LO QUE NECESITAS SABER DE LA PRÓXIMA REUNIÓN ── */}
      {proxima && conEsaPersona.length > 0 && (
        <Card>
          <CardBody>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Antes de ver a {proxima.conQuien}
            </p>
            <ul className="mt-1 space-y-0.5 text-sm text-slate-600 dark:text-slate-300">
              {conEsaPersona.slice(0, 4).map((a) => (
                <li key={a.id}>
                  <button
                    onClick={() => setAbierto(a.id)}
                    className="text-left underline-offset-2 hover:underline"
                  >
                    · {a.descripcion}
                    {a.montoCentavos ? ` (${plata(a.montoCentavos, a.moneda)})` : ''}
                  </button>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {/* ── ESCAPANDO + TU DÍA, LADO A LADO EN ESCRITORIO ──
          A 1440 px las dos tarjetas apiladas dejaban la mitad derecha en
          blanco y obligaban a bajar para ver el día. */}
      <div className="grid gap-4 xl:grid-cols-2">
      <Card>
        <CardBody className="space-y-1">
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Se te está escapando
          </p>
          {senales.isPending ? (
            <Spinner />
          ) : listaSenales.length === 0 ? (
            <p className="py-4 text-sm text-slate-400">
              Nada por ahora. Si alguien te escribe y no le contestas, aparece aquí.
            </p>
          ) : (
            listaSenales.slice(0, 6).map((s, i) => (
              <button
                key={`${s.tipo}-${s.conversationId ?? i}`}
                onClick={() =>
                  s.conversationId ? navegar(`/chats/${s.conversationId}`) : navegar('/me-toca')
                }
                className="flex w-full min-h-[44px] items-start gap-2.5 border-b border-slate-100 py-2 text-left last:border-0 dark:border-slate-800"
              >
                <span className="mt-0.5 shrink-0 text-slate-400">{ICONO_SENAL[s.tipo]}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-slate-800 dark:text-slate-100">
                    {s.titulo}
                  </span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">
                    {s.detalle}
                  </span>
                </span>
              </button>
            ))
          )}
          {(senales.data?.noPudeVer.length ?? 0) > 0 && (
            <p className="rounded bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              No pude revisar: {senales.data?.noPudeVer.join(', ')}.
            </p>
          )}
        </CardBody>
      </Card>

      {/* ── TU DÍA ── */}
      <Card>
        <CardBody className="space-y-0">
          <p className="mb-1 text-sm font-semibold text-slate-700 dark:text-slate-200">Tu día</p>
          {asuntos.isPending ? (
            <Spinner />
          ) : deHoy.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">
              No tienes nada agendado para hoy.
            </p>
          ) : (
            deHoy.map((a) => (
              <TarjetaDeAsunto
                key={a.id}
                asunto={a}
                onAbrir={setAbierto}
                onAprobar={(id) => mover.mutate({ id, accion: 'aprobar' })}
                onDescartar={(id) => mover.mutate({ id, accion: 'descartar' })}
              />
            ))
          )}
        </CardBody>
      </Card>

      </div>

      {/* ── LOS TRES WIDGETS ──
          Cada tarjeta ABRE su detalle en el centro, en vez de llevarse al
          dueño a otra pantalla. Mandarlo a `Me toca` con un filtro puesto le
          hacía perder el contexto de "Hoy" para leer cuatro filas. */}
      <Widget
        abierto={widget === 'urgente'}
        titulo={`Urgente · ${urgentes.length}`}
        bajada={
          urgentes.length === 0
            ? 'Nada urgente ahora mismo.'
            : 'Lo vencido primero; después, lo que marcó como urgente.'
        }
        onCerrar={() => setWidget(null)}
      >
        {urgentes.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">
            Nada urgente. Lo que venza o suba a urgente aparece acá.
          </p>
        ) : (
          urgentes.map((a) => (
            <TarjetaDeAsunto
              key={a.id}
              asunto={a}
              conFecha
              onAbrir={(id) => {
                setWidget(null);
                setAbierto(id);
              }}
              onAprobar={(id) => mover.mutate({ id, accion: 'aprobar' })}
              onDescartar={(id) => mover.mutate({ id, accion: 'descartar' })}
            />
          ))
        )}
      </Widget>

      <Widget
        abierto={widget === 'proximo'}
        titulo={proxima ? `${proxima.conQuien} · ${hora12(proxima.fechaCompromiso)}` : 'Lo próximo'}
        bajada={
          proxima
            ? conEsaPersona.length > 0
              ? `Tienes ${conEsaPersona.length} ${conEsaPersona.length === 1 ? 'cosa abierta' : 'cosas abiertas'} con esa persona.`
              : 'No tienes nada más abierto con esa persona.'
            : 'No hay nada agendado por delante.'
        }
        onCerrar={() => setWidget(null)}
      >
        {!proxima ? (
          <p className="py-10 text-center text-sm text-slate-400">
            Cuando Athena detecte una cita, aparece acá.
          </p>
        ) : (
          // Lo próximo PRIMERO, y debajo lo que le debes a esa persona: es lo
          // que convierte "tienes una reunión" en "llegas sabiendo".
          [proxima, ...conEsaPersona].map((a) => (
            <TarjetaDeAsunto
              key={a.id}
              asunto={a}
              conGestos={false}
              onAbrir={(id) => {
                setWidget(null);
                setAbierto(id);
              }}
            />
          ))
        )}
      </Widget>

      <Widget
        abierto={widget === 'dinero'}
        titulo={totales.length > 0 ? `Te deben ${totales.join(' y ')}` : 'Nada por cobrar'}
        bajada={
          dinero.length > 0
            ? 'De mayor a menor. Lo vencido lleva su marca.'
            : 'Cuando alguien te prometa un pago, aparece acá.'
        }
        onCerrar={() => setWidget(null)}
      >
        {dinero.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">
            No tienes cobros registrados.
          </p>
        ) : (
          // De MAYOR a menor: es lo que pidió. Por fecha, el de RD$45.000
          // podía quedar debajo del de RD$300.
          [...dinero]
            .sort((x, y) => (y.montoCentavos ?? 0) - (x.montoCentavos ?? 0))
            .map((a) => (
              <TarjetaDeAsunto
                key={a.id}
                asunto={a}
                conFecha
                onAbrir={(id) => {
                  setWidget(null);
                  setAbierto(id);
                }}
                onAprobar={(id) => mover.mutate({ id, accion: 'aprobar' })}
              />
            ))
        )}
      </Widget>

      <HojaDelAsunto asuntoId={abierto} onCerrar={() => setAbierto(null)} />
    </div>
  );
}
